// RequestDashboard.tsx
import React, {useEffect, useRef, useState} from "react";
import {Box, Typography} from "@mui/material";
import {useNavigate} from "react-router-dom";
import NavBar from "../NavBar/NavBar";
import {getIdMeBaseApiUrl} from "../../../utils/UrlConstants";
import {UUID} from "node:crypto";
import {toast, ToastContainer} from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./RequestDashboard.scss";
import Dropdown from "../../../utils/components/Dropdown";
import MultiSelectDropdown from "../../../utils/components/MultiSelectDropdown";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Button from "../../common/Button";
import FooterData from "../NavBar/FooterData";
import {checkUserAuthentication, handleUnauthenticatedUser} from "../../../utils/authUtils";
import {base64Utils} from "../../../utils/base64Utils";
import { useIdentityAppService } from "../../../services/IdentityServices";

// Types for API responses
interface ApplicationDto {
    appCd: string;
    applicationName: string;
    applicationDesc: string;
    orgCd: string;
    orgName: string;
    appOwner: string;
    operationalFlag: string;
    createdDate: string;
    modifiedDate: string;
    allowExternalAccess: string;
    allowInternalAccess: string;
}

interface EnvironmentDto {
    appEnvCd: string;
    envName: string;
    envDesc: string;
    appCd: string;
    applicationName: string;
    idpCd: string;
    idpName: string;
    ssoType: string;
    appIdpIdentifier: string;
    uri: string;
    operationalFlag: string;
    createdDate: string;
    modifiedDate: string;
}

interface RoleDto {
    roleCd: string;
    roleName: string;
    roleDesc: string;
    appCd: string;
    applicationName: string;
    appEnvCd: string;
    envName: string;
    operationalFlag: string;
    createdDate: string;
    modifiedDate: string;
    roleIdentifier: string;
}

interface ApplicationListResponse {
    success: boolean;
    message: string;
    applications: ApplicationDto[];
    count: number;
    timestamp: string;
}

interface EnvironmentListResponse {
    success: boolean;
    message: string;
    environments: EnvironmentDto[];
    count: number;
    timestamp: string;
}

interface RoleListResponse {
    success: boolean;
    message: string;
    roles: RoleDto[];
    count: number;
    timestamp: string;
}

// Add User Search Response interface
interface UserSearchResponse {
    success: boolean;
    message: string;
    users: any[];
    count: number;
    timestamp: string;
}

interface MultiSelectChangeEvent {
    target: {
        name: string;
        value: (string | number)[];
    };
}

interface FormErrors {
    [key: string]: string;
}

const RequestDashboard: React.FC = () => {
    const [userName, setUserName] = useState<string>();
    const [userId, setUserId] = useState<UUID>();
    const [selectedApplication, setSelectedApplication] = useState<string>('');
    const [selectedEnvironment, setSelectedEnvironment] = useState<string>('');
    const [selectedRoles, setSelectedRoles] = useState<(string | number)[]>([]);
    const [externalGroups, setExternalGroups] = useState<string[]>([]);

    // Data state using new structure
    const [applications, setApplications] = useState<ApplicationDto[]>([]);
    const [environments, setEnvironments] = useState<EnvironmentDto[]>([]);
    const [roles, setRoles] = useState<RoleDto[]>([]);

    // Loading state
    const [loadingApplications, setLoadingApplications] = useState(false);
    const [loadingEnvironments, setLoadingEnvironments] = useState(false);
    const [loadingRoles, setLoadingRoles] = useState(false);

    // Error state
    const [errors, setErrors] = useState<FormErrors>({});
    const [error, setError] = useState<string | null>(null);

    const [notes, setNotes] = useState<string>('');
    const navigate = useNavigate();
    const applicationRefNotes = useRef(null);

    const [currentUser, setCurrentUser] = useState<any>(null);

    // Helper function to get current user data from sessionStorage
    const getCurrentUserFromSession = () => {
        try {
            const storedUserData = sessionStorage.getItem('currentUserData');
            return storedUserData ? JSON.parse(storedUserData) : null;
        } catch (error) {
            console.error("Error parsing current user data from session:", error);
            return null;
        }
    };


    // Add new state for user search data
    const [sessionUserData, setSessionUserData] = useState<any>(null);

    // Function to load user data from session storage
    const loadUserDataFromSession = () => {
        try {
            const storedUserData = sessionStorage.getItem('currentUserData');
            if (storedUserData) {
                const userData = JSON.parse(storedUserData);
                setSessionUserData(userData);
                console.log("Loaded user data from session:", userData);
            }
        } catch (error) {
            console.error("Error loading user data from session:", error);
        }
    };

    useEffect(() => {
        if (!checkUserAuthentication()) {
            handleUnauthenticatedUser();
            return;
        }

        const externalGroupsData = sessionStorage.getItem("external_groups");
        const externalGroupsList = externalGroupsData ? externalGroupsData.split(',') : [];
        setExternalGroups(externalGroupsList);

        const externalTokenString = sessionStorage.getItem("external_token");
        const externalToken = externalTokenString ? JSON.parse(externalTokenString) : null;
        setUserName(externalToken?.username);
        setUserId(externalToken?.sub);

        // Load user data from session storage first
        loadUserDataFromSession();

        // Load applications on component mount
        loadApplications();
    }, []);

    // Reset environment and roles when application changes
    useEffect(() => {
        if (selectedApplication) {
            loadEnvironments(selectedApplication);
            setSelectedEnvironment('');
            setSelectedRoles([]);
            setRoles([]);
        } else {
            setEnvironments([]);
            setSelectedEnvironment('');
            setSelectedRoles([]);
            setRoles([]);
        }
    }, [selectedApplication]);

    // Reset roles when environment changes
    useEffect(() => {
        if (selectedApplication && selectedEnvironment) {
            // For external users, you might want to filter by a specific IDP
            // For now, we'll load all roles, but you can modify this to pass an idpName
            loadRoles(selectedApplication, selectedEnvironment, 'External');
            setSelectedRoles([]);
        } else {
            setRoles([]);
            setSelectedRoles([]);
        }
    }, [selectedApplication, selectedEnvironment]);

    // Load applications from API
    const loadApplications = async () => {
        try {
            setLoadingApplications(true);
            setError(null);

            const response = await useIdentityAppService().getExternalAccessApplications();

            if (response.ok) {
                const data: ApplicationListResponse = await response.json();

                // Filter applications based on external groups (applications user doesn't have access to)
                const filtered = data.applications.filter(
                    (app: ApplicationDto) => !externalGroups.includes(app.applicationName)
                );

                setApplications(filtered);
                console.log(`Loaded ${filtered.length} applications available for external access request`);
            } else {
                const errorData = await response.json();
                setError(errorData.error || 'Failed to load applications');
            }
        } catch (err) {
            console.error('Error loading applications:', err);
            setError(err instanceof Error ? err.message : 'Network error occurred while loading applications');
        } finally {
            setLoadingApplications(false);
        }
    };

    // Load environments for selected application
    const loadEnvironments = async (appCd: string) => {
        try {
            setLoadingEnvironments(true);

            const response = await useIdentityAppService().getApplicationEnvironmentsByAppCd(appCd);

            if (response.ok) {
                const data: EnvironmentListResponse = await response.json();
                setEnvironments(data.environments);
                console.log(`Loaded ${data.environments.length} environments for application ${appCd}`);
            } else {
                const errorData = await response.json();
                console.error('Failed to load environments:', errorData);
                setEnvironments([]);
            }
        } catch (err) {
            console.error('Error loading environments:', err);
            setEnvironments([]);
        } finally {
            setLoadingEnvironments(false);
        }
    };

    // Load roles for selected application and environment, optionally filtered by IDP name
    const loadRoles = async (appCd: string, envCd: string, idpName?: string) => {
        try {
            setLoadingRoles(true);

            // Build URL with optional idpName query parameter
            let url = `${getIdMeBaseApiUrl()}/application/${appCd}/environments/${envCd}/roles`;
            if (idpName && idpName.trim() !== '') {
                url += `?idpName=${encodeURIComponent(idpName)}`;
            }

            const response = await useIdentityAppService().getApplicationEnvironmentsByIdpName(url);

            if (response.ok) {
                const data: RoleListResponse = await response.json();
                setRoles(data.roles);

                const logMessage = idpName
                    ? `Loaded ${data.roles.length} roles for application ${appCd}, environment ${envCd}, and IDP ${idpName}`
                    : `Loaded ${data.roles.length} roles for application ${appCd} and environment ${envCd}`;
                console.log(logMessage);
            } else {
                const errorData = await response.json();
                console.error('Failed to load roles:', errorData);
                setRoles([]);
            }
        } catch (err) {
            console.error('Error loading roles:', err);
            setRoles([]);
        } finally {
            setLoadingRoles(false);
        }
    };

    // Handle application dropdown change
    const handleApplicationChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const appCd = event.target.value;
        setSelectedApplication(appCd);

        // Clear errors when user makes selections
        if (errors.application) {
            setErrors(prev => ({...prev, application: ''}));
        }
    };

    // Handle environment dropdown change
    const handleEnvironmentChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const envCd = event.target.value;
        setSelectedEnvironment(envCd);

        // Clear errors when user makes selections
        if (errors.environment) {
            setErrors(prev => ({...prev, environment: ''}));
        }
    };

    // Handle role multi-select change
    const handleRoleChange = (event: MultiSelectChangeEvent) => {
        const roleIds = event.target.value;
        setSelectedRoles(roleIds);

        // Clear errors when user makes selections
        if (errors.roles) {
            setErrors(prev => ({...prev, roles: ''}));
        }
    };

    // Validate form
    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};

        if (!selectedApplication) {
            newErrors.application = 'Please select an application';
        }

        if (!selectedEnvironment) {
            newErrors.environment = 'Please select an environment';
        }

        if (selectedRoles.length === 0) {
            newErrors.roles = 'Please select at least one role';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            toast.error('Please fill in all required fields');
            return;
        }

        const token = sessionStorage.getItem("external_token");
        let parsedToken = null;
        if (token) {
            parsedToken = JSON.parse(token);
        }

        try {
            let userResult = null;
            let currentUserToUse = null;

            // Check if we have session user data
            if (sessionUserData) {
                // Use existing session user data
                console.log("Using existing session user data:", sessionUserData);
                userResult = sessionUserData;

                currentUserToUse = {
                    userId: sessionUserData.userId || parsedToken?.sub || userId,
                    userLogin: sessionUserData.userName || parsedToken?.username || userName,
                    userType: sessionUserData.userType || parsedToken?.extension_userType || "External",
                    firstName: sessionUserData.firstName || parsedToken?.given_name || "",
                    lastName: sessionUserData.lastName || parsedToken?.family_name || "",
                    userEmail: sessionUserData.email || parsedToken?.email || "",
                    userPhone: sessionUserData.phone || parsedToken?.mobile || "",
                    notes: sessionUserData.notes || parsedToken?.notes || ""
                };
            } else {
                // No session data exists, call check-or-create-user API
                console.log("No session user data found, calling check-or-create-user API");

                const userRequest = {
                    userType: parsedToken?.extension_userType || "External",
                    userName: parsedToken?.username || userName,
                    firstName: parsedToken?.given_name || "",
                    lastName: parsedToken?.family_name || "",
                    email: parsedToken?.email || "",
                    phone: parsedToken?.mobile || "",
                    notes: parsedToken?.notes || "",
                    userId: parsedToken?.sub || userId,
                    organizationName: parsedToken?.extension_organizationName || "",
                    organizationType: parsedToken?.extension_partnerType || "",
                    stateIssuedId: parsedToken?.extension_stateId || "",
                    organizationNotes: parsedToken?.organizationNotes || ""
                };

                console.log("Checking or creating user:", userRequest);

                const userResponse = await useIdentityAppService().postUserCheckOrCreate(JSON.stringify(userRequest));

                if (!userResponse.ok) {
                    const errorData = await userResponse.json();
                    console.error("Failed to check or create user:", errorData);
                    toast.error("Failed to verify user information. Please try again.");
                    return;
                }

                userResult = await userResponse.json();
                console.log("User check/create result:", userResult);

                // Store the new user data in session for future use
                setSessionUserData(userResult);
                sessionStorage.setItem('currentUserData', JSON.stringify(userResult));

                // Create user object to match AssignRoles format
                currentUserToUse = {
                    userId: userResult.userId || parsedToken?.sub || userId,
                    userLogin: userRequest.userName,
                    userType: userRequest.userType,
                    firstName: userRequest.firstName,
                    lastName: userRequest.lastName,
                    userEmail: userRequest.email,
                    userPhone: userRequest.phone,
                    notes: userRequest.notes
                };
            }

            setCurrentUser(currentUserToUse);

            // Step 3: Prepare assignment data exactly like AssignRoles
            const assignmentData = {
                userIds: [currentUserToUse.userId],
                applicationCode: selectedApplication,
                environmentCode: selectedEnvironment,
                roleIds: selectedRoles,
                userType: currentUserToUse.userType,
                assignedTo: currentUserToUse.userLogin, // For external users, they assign to themselves
                notes: notes.trim() ? base64Utils.encode(notes.trim()) : null,
                startDate: new Date().toISOString().split('T')[0], // Current date
                assignOption: 'immediate',
                endDate: (() => {
                    const today = new Date();
                    const endDate = new Date(today);
                    endDate.setDate(today.getDate() + 364); // Add 364 days
                    return endDate.toISOString().split('T')[0];
                })()
            };

            console.log("Submitting role assignment:", assignmentData);

            // Call the same API endpoint as AssignRoles
            const response = await useIdentityAppService().postUserAssignRoles(JSON.stringify(assignmentData));

            if (response.ok) {
                const data = await response.json();
                console.log('Role assignment response:', data);

                if (data.success) {
                    toast.success("Role assignment request submitted successfully!");

                    // Reset form
                    setSelectedApplication('');
                    setSelectedEnvironment('');
                    setSelectedRoles([]);
                    setNotes('');
                } else {
                    toast.error(data.message || 'Role assignment request failed');

                    // Show individual errors if available
                    if (data.errors && data.errors.length > 0) {
                        data.errors.forEach((error: string) => {
                            toast.error(error, {autoClose: 6000});
                        });
                    }
                }
            } else {
                // Handle HTTP errors (non-200 responses including 500)
                let errorData: any;
                try {
                    errorData = await response.json();
                } catch (parseError) {
                    errorData = {
                        error: 'Unknown error occurred',
                        message: `HTTP ${response.status}: ${response.statusText}`
                    };
                }

                console.error('Role assignment failed:', {
                    status: response.status,
                    statusText: response.statusText,
                    errorData: errorData
                });

                // Show main error toast
                toast.error('Role assignment failed.', { autoClose: 7000 });

                // If the response contains detailed error information, display it
                if (errorData.errors && errorData.errors.length > 0) {
                    errorData.errors.forEach((err: string) => {
                        console.error('Role assignment error:', err);
                        toast.error(err, { autoClose: 6000 });
                    });
                }

                // Show failed assignment details if available
                if (errorData.assignmentDetails && errorData.assignmentDetails.length > 0) {
                    const failedAssignments = errorData.assignmentDetails.filter(
                        (detail: any) => detail.status === 'FAILED'
                    );

                    if (failedAssignments.length > 0) {
                        failedAssignments.forEach((assignment: any) => {
                            console.error('Failed assignment:', assignment);
                            toast.error(
                                `Failed to assign ${assignment.roleName} to ${assignment.userName}: ${assignment.message}`,
                                { autoClose: 8000 }
                            );
                        });
                    }
                }
            }

        } catch (error) {
            console.error("Error submitting role assignment request:", error);
            toast.error("Role assignment failed.");
        }
    };

    function joinClasses(...classes: (string | undefined)[]): string {
        return classes.filter(Boolean).join(" ");
    }

    const wrapperClasses = joinClasses("usa-fieldset usa-form-group");

    const handleRichTextChangeAppNotes = (event: any) => {
        setNotes(event);
        if (applicationRefNotes.current != null) {
            const appDetails = event;
        }
    };

    // Convert data to dropdown choices
    const applicationChoices = applications.map(app => ({
        value: app.appCd,
        label: app.applicationDesc ? `${app.applicationName} - ${app.applicationDesc}` : app.applicationName
    }));

    const environmentChoices = environments.map(env => ({
        value: env.appEnvCd,
        label: env.envName
    }));

    const roleChoices = roles.map(role => ({
        value: role.roleCd,
        label: role.roleName
    }));

    return (
        <React.Fragment>
            <NavBar/>
            <ToastContainer position="top-right" autoClose={false} closeButton closeOnClick={false}
                            hideProgressBar/>
            <Box sx={{width: '100%'}}>
                <Box sx={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
                    <Typography variant="h5" sx={{ marginBottom: '30px', color: '#00607f', fontWeight: 'bold', textAlign: 'center' }}>
                        New Access Request
                    </Typography>

                    {/* Error Message */}
                    {error && (
                        <Box sx={{
                            marginBottom: '20px',
                            padding: '16px',
                            backgroundColor: '#ffebee',
                            borderRadius: '4px',
                            border: '1px solid #ef5350'
                        }}>
                            <Typography sx={{ color: '#c62828', fontWeight: 'bold' }}>
                                Error: {error}
                            </Typography>
                        </Box>
                    )}

                    {/* Row 1: Application Name */}
                    <Box sx={{ marginBottom: '20px' }}>
                        <Dropdown
                            choices={applicationChoices}
                            emptyChoiceLabel={loadingApplications ? "Loading applications..." : "Select Application Name"}
                            label={"Application Name"}
                            name={"appName"}
                            value={selectedApplication}
                            onChange={handleApplicationChange}
                            mandatoryLabel={true}
                            disabled={loadingApplications}
                        />
                        {errors.application && (
                            <Typography sx={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
                                {errors.application}
                            </Typography>
                        )}
                    </Box>

                    {/* Row 2: Environment */}
                    <Box sx={{ marginBottom: '20px' }}>
                        <Dropdown
                            choices={environmentChoices}
                            emptyChoiceLabel={
                                !selectedApplication
                                    ? "Select application first"
                                    : loadingEnvironments
                                        ? "Loading environments..."
                                        : environments.length === 0
                                            ? "No environments available"
                                            : "Select Environment"
                            }
                            label={"Environment"}
                            name={"environment"}
                            value={selectedEnvironment}
                            disabled={!selectedApplication || loadingEnvironments}
                            onChange={handleEnvironmentChange}
                            mandatoryLabel={true}
                        />
                        {errors.environment && (
                            <Typography sx={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
                                {errors.environment}
                            </Typography>
                        )}
                    </Box>

                    {/* Row 3: Roles */}
                    <Box sx={{ marginBottom: '20px' }}>
                        <MultiSelectDropdown
                            choices={roleChoices}
                            emptyChoiceLabel={
                                !selectedEnvironment
                                    ? "Select environment first"
                                    : loadingRoles
                                        ? "Loading roles..."
                                        : roles.length === 0
                                            ? "No roles available"
                                            : "Select Roles"
                            }
                            label={"Roles"}
                            name={"roles"}
                            value={selectedRoles}
                            onChange={handleRoleChange}
                            disabled={!selectedEnvironment || loadingRoles}
                            mandatoryLabel={true}
                            searchable={true}
                            showSelectedAsTags={true}
                            allowSelectAll={true}
                        />
                        {errors.roles && (
                            <Typography sx={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
                                {errors.roles}
                            </Typography>
                        )}
                    </Box>

                    {/* Row 4: Request Notes */}
                    <Box sx={{ marginBottom: '30px' }}>
                        <fieldset className={wrapperClasses}>
                            <span>
                                <label htmlFor="my-editor">
                                    <b>Request Notes:</b>
                                </label>
                                <ReactQuill
                                    className="my-editor"
                                    style={{maxHeight: "100px", overflowY: "auto"}}
                                    value={notes}
                                    ref={applicationRefNotes}
                                    onChange={handleRichTextChangeAppNotes}
                                    modules={{
                                        toolbar: [
                                            ["bold", "italic", "underline", "strike"],
                                            [{list: "ordered"}, {list: "bullet"}],
                                            [{align: []}],
                                            ["clean"],
                                        ],
                                    }}
                                />
                            </span>
                        </fieldset>
                    </Box>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: '15px', justifyContent: 'center', marginTop: '30px' }}>
                        <Button
                            onClick={() => navigate("/")}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            className={"primary-button"}
                            disabled={!selectedApplication || !selectedEnvironment || selectedRoles.length === 0}
                        >
                            Submit
                        </Button>
                    </Box>
                </Box>
            </Box>
            <FooterData />
        </React.Fragment>
    );
};

export default RequestDashboard;
